// ? [REFACTOR] too much in 1 file, split up into multiple modules
// ? [REVISIT] The hack for Slate double render & fixing types
import { differenceBy, noop } from 'lodash-es';

import { ALO_MOVES_30_GWP, EXCHANGE_ONLY_TAG, FINAL_SALE_TAG } from '../constants';
import { useState, useEffect } from 'react';
import { fireGA4ecommerceAddOrRemoveProductEvent, pushEventToGTMDataLayer } from './../gtm-data-layer';
import { AddedToCartProduct } from '../../templates/_shared/ui-components/ATCModal/types';
import { IS_NATIVE_APP_CLIENT, getOnlineSalePricing, heapTrack } from '../helpers';
import { initializeCartAnalytics } from './cart-analytics';
import { gql } from '@apollo/client';
import { ga4options } from '../../templates/product-listing-page/quick-add/QuickAddPanel';
import { GWPstate } from '../../sections/header/Cart/components/GWP/gwpState';
import { GWPandLoyaltyGeoSettings } from '../../globals';
import { toJS } from 'mobx';
import { fireHeapRedeemGWPevent } from '../heap-events';
import {
  dispatchOpenVariantSelectorDropdown,
  fetchExtendedProductData,
} from 'src/scripts/sections/header/Cart/components/CartImpulseBuy/utils';
import { prependLocaleToPath } from '../localization';
import { updateCartLineItem, cartNoteUpdate, addLineItemToCart, LineItem, LineItems } from './cart-apis';
import { transformCartApiResponse } from './transform-cart-api-response';
import {
  getCartDataFromLocalStore,
  splitAtFirstColon,
  transformArrayToObject,
  transformCartItemAttributesToFitCartLinesAddMutation,
} from './cart-api-utils';
import { notNil } from '../typescriptUtils';

export const CHECKOUT_TEXT = 'CHECKOUT';
export const PROCESSING_TEXT = 'PROCESSING';
export const isLoopReturnsActive = (): boolean => window?.LoopOnstore?.isActive() ?? false;
export const isCartApiCheckoutButtonEnabled = window.getSiteSettings()?.enableCartApiCheckoutButton ?? false;
export const submitLoopStore = () => window.LoopOnstore?.submit();

// Removed the dependency of AJAX cart Object from server_cart_data.
// Only the settings and customerTags are reading from the server_cart_data variable,
// which are common for both AJAX and Cart API.
const defaultData = {
  settings: window.server_cart_data?.settings,
  customerTags: window.server_cart_data?.customerTags,
  cart: {
    note: '',
    attributes: {},
    original_total_price: 0,
    total_price: 0,
    total_discount: 0,
    item_count: 0,
    items: [],
    currency: window?.Shopify?.country ?? 'US',
    items_subtotal_price: 0,
    checkout_charge_amount: 0,
  },
};
// Data fetching & processing ------------------------------------
//@ts-ignore // ? [REVISIT] if there is no window_cart_data ... we're not setup to handle it, until we pull everything form the api & not from liquid
export const serverCartData: WindowExtended.ServerCartData = defaultData;
const CART_STATE = {
  currentData: serverCartData.cart,
};

const computeHasSelectedTag = (tags: string[], searchTag: string) => tags.includes(searchTag);

const computeSubtotal = (cartItems: CartItem[]) =>
  cartItems.reduce((total, item) => total + item.price * item.quantity, 0);

const computeTotalSavings = (cartItems: CartItem[]) =>
  cartItems.reduce((total, item) => {
    const diffPrice = getOnlineSalePricing(item.tags, item.original_price);
    let itemDiscount;
    if (diffPrice) {
      const price = item.displayPrice ?? item.original_price;
      itemDiscount = (price - diffPrice * 100) * item.quantity;
    } else if (item.displayPrice) {
      itemDiscount = (item.displayPrice - item.price) * item.quantity;
    } else {
      return total;
    }
    return total + itemDiscount;
  }, 0);

export const processCartData = (data: ShopifyCartData): CartData => {
  const regularItems = data.items
    .filter((item) => item.handle !== null && item.options_with_values !== null)
    .filter((item) => {
      return !item.properties?.__lion_context && !item.properties?._isGWP;
    })
    .map((item) => {
      const actualPrice = item.final_price;
      // We are passing the displayPrice, tags and collections from Cart API response itself
      const displayPrice = item.displayPrice ?? null;
      const { tags, collections, quantity } = item;
      const showFinalSaleTreatment =
        (quantity !== 0 && actualPrice + (item.total_discount ?? 0) / 100 <= 0.4 * (displayPrice || actualPrice)) ||
        (!!tags && computeHasSelectedTag(tags, FINAL_SALE_TAG));
      const colorOption = item.options_with_values.find((option) => option.name === 'Color');
      const titleWithoutColor = (colorOption
        ? item.product_title.replace(`- ${colorOption.value}`, '')
        : item.product_title
      ).trim();
      const showExchangeOnlyMessage = !!tags && computeHasSelectedTag(tags, EXCHANGE_ONLY_TAG);
      // ? [REVISIT] we could default `displayPrice` to `price` here
      return {
        ...item,
        tags,
        collections,
        product_title: titleWithoutColor,
        price: actualPrice,
        showFinalSaleTreatment,
        showExchangeOnlyMessage,
        properties: item.properties || {},
      };
    });

  const giftItemsAndLoyaltyRewards = data.items
    .map((item) => {
      return { ...item, properties: item.properties || {} };
    })
    .filter((item) => {
      return item.properties.__lion_context || item.properties._isGWP;
    });

  const subtotal = computeSubtotal(regularItems);
  const totalSavings = computeTotalSavings(regularItems);
  const regularItemsCount = regularItems.reduce((total, item) => total + item.quantity, 0);
  // cart count includes all items and GWPs except for 30 days of alo moves
  const count =
    regularItemsCount +
    giftItemsAndLoyaltyRewards?.reduce((total, item) => {
      if (item.sku !== ALO_MOVES_30_GWP) {
        return total + item.quantity;
      }
      return total;
    }, 0);

  const { checkoutUrl, note } = data;
  // To include sold-out item
  const allItems = [...giftItemsAndLoyaltyRewards, ...regularItems];
  // Calculate total quantity by summing up the quantity for each item
  const totalCartItemsQty = allItems.reduce((totalCount, item) => {
    return totalCount + (item.quantity || 0); // Default to 0 if quantity is zero to count sold-out item.
  }, 0);

  return {
    regularItems,
    giftItemsAndLoyaltyRewards,
    subtotal,
    totalSavings,
    count,
    note,
    checkoutUrl,
    totalCartItemsQty,
  };
};

// additionally we should do a client side api call to ensure any issues with server side data staleness & to account for `bfcache` more elegantly
export const getCartData = () => processCartData(CART_STATE.currentData);

// Cart sync (lightweight pub/sub model)
export type WatchCartCallback = (newCartData: CartData) => void;
const CART_OBSERVERS: WatchCartCallback[] = [];

export const watchCart = (onChangeCallback: WatchCartCallback) => {
  CART_OBSERVERS.push(onChangeCallback);
  const disconnectObserver = () => {
    try {
      const indexToRemove = CART_OBSERVERS.findIndex((item) => item === onChangeCallback);
      CART_OBSERVERS.splice(indexToRemove, 1);
    } catch (err) {}
  };
  return disconnectObserver;
};

const publishChanges = (newData: ShopifyCartData = CART_STATE.currentData) => {
  CART_STATE.currentData = newData;
  CART_OBSERVERS.forEach((callback) => callback(processCartData(newData)));
};
export const forceCartObserversRefresh = () => {
  publishChanges();
};

export const calculateSubtotalForRewards = (cartData: CartData): number => {
  const subtotalOfExludedProductsFromCalculation = calculateSubtotalOfExclusions(cartData);
  const total = cartData.regularItems.reduce((total, item) => {
    return total + item.original_price * item.quantity;
  }, 0);
  return (total - subtotalOfExludedProductsFromCalculation) / 100;
};

export const calculateSubtotalOfExclusions = (cartData: CartData) => {
  return cartData.regularItems
    .filter(
      (item) =>
        item.gift_card ||
        item.vendor.toLowerCase() === 'alo moves' ||
        item.sku === 'ALO01001YR' ||
        item.collections?.includes('point-restrictions')
    ) // ALO01001YR is AloMoves annual membership
    .flatMap((excludedProduct) => excludedProduct.final_line_price)
    .reduce((a, b) => a + b, 0);
};

export const removeGWPitemsFromCart = async (countryCode: string | null) => {
  if (!countryCode) return;
  const cartData = processCartData(CART_STATE.currentData);
  const { giftItemsAndLoyaltyRewards } = cartData;

  const giftsToRemove = giftItemsAndLoyaltyRewards.filter((item) => item.properties?._isGWP);
  if (giftsToRemove.length > 0) {
    let updates: Record<string, number> = {};
    const variantIds = giftsToRemove?.map((reward) => String(reward.line_id));

    giftsToRemove?.forEach((reward) => (updates[reward.variant_id] = 0));
    if (variantIds.length > 0) {
      await updateCartLineItem(variantIds, 0)
        .then((newShopifyCartDataWithUpdates) => {
          publishChanges(
            transformCartApiResponse(newShopifyCartDataWithUpdates?.cart, {
              warnings: newShopifyCartDataWithUpdates?.warnings,
            })
          );
        })
        .catch((err: Error) => {
          heapTrack('Failed to remove lion items from cart on country change', { err: err?.message });
        });
    }
  }
};

// ? [REVISIT] `reSyncCart` is a bad name; this pattern (always shows the 'truth' vs an 'optimistic update') is inefficient w/ network calls but requires less data passing & is safer w/o explicit error handling
export const reSyncCart = async (newShopifyCartData: ShopifyCartData, { forceUpdateSpecialItems = false } = {}) => {
  let shopifyCartData = newShopifyCartData;
  const oldCartData = processCartData(CART_STATE.currentData);
  const newCartData = processCartData(shopifyCartData);

  const { shouldRenderGWPforINTL } = await GWPandLoyaltyGeoSettings();
  const GWPitems = toJS(GWPstate.currentGifts);

  const { giftItemsAndLoyaltyRewards } = newCartData;
  const subtotalForRewards = calculateSubtotalForRewards(newCartData);
  const validRewards = shouldRenderGWPforINTL ? GWPitems?.filter((reward) => reward.enabled_for_intl) : GWPitems;

  const loyaltyGiftItemsVariantIDsInCart = giftItemsAndLoyaltyRewards.flatMap((item) => item.variant_id);
  const prevRemovedGWPs = localStorage?.getItem('giftsRemovedByUser')?.split(',');
  const rewardsToRedeem = validRewards?.filter((reward) => {
    return (
      !isLoopReturnsActive() &&
      reward.amount_to_spend <= subtotalForRewards &&
      reward.auto_redeem &&
      !prevRemovedGWPs?.includes(reward.variant_id) &&
      !loyaltyGiftItemsVariantIDsInCart.includes(Number(reward.variant_id))
    );
  });

  // To remove redeemed gift from cart if any product has sold-out and its quantity is zero in cart.
  const loyaltyGiftItemToRemove = giftItemsAndLoyaltyRewards
    .filter((item) => {
      const reward = validRewards?.find((validReward) => Number(validReward.variant_id) === item.variant_id);
      return reward && reward.amount_to_spend > subtotalForRewards;
    })
    .flatMap((item) => item.line_id)
    .filter(notNil);

  if (loyaltyGiftItemToRemove.length > 0) {
    try {
      const updateLineItemResponse = await updateCartLineItem(loyaltyGiftItemToRemove, 0);
      shopifyCartData = transformCartApiResponse(updateLineItemResponse?.cart, {
        warnings: updateLineItemResponse?.warnings,
      });
    } catch (error) {
      heapTrack('Failed to remove gift from cart', { err: 'Gift update error' });
    }
  }

  // ? [REVISIT] Why we need to check for oldCartData.count === 0 || newCartData.count === 0   ???
  const needsToUpdateSpecialItems =
    (rewardsToRedeem && rewardsToRedeem.length > 0) || oldCartData.count === 0 || newCartData.count === 0;

  if (!(needsToUpdateSpecialItems || forceUpdateSpecialItems)) {
    publishChanges(shopifyCartData);
  } else {
    let lines: LineItems = [];
    if (rewardsToRedeem && rewardsToRedeem.length > 0) {
      lines = rewardsToRedeem.map((reward) => {
        return {
          merchandiseId: Number(reward.variant_id),
          quantity: 1,
        };
      });
    }
    if (lines.length > 0) {
      try {
        const addLineItemToCartResponse = await addLineItemToCart(lines);
        const newShopifyCartDataWithUpdates = transformCartApiResponse(addLineItemToCartResponse?.cart, {
          warnings: addLineItemToCartResponse?.warnings,
        });
        publishChanges(newShopifyCartDataWithUpdates);
        rewardsToRedeem?.forEach((reward) =>
          fireHeapRedeemGWPevent({ productTitle: reward.title, variantId: reward.variant_id, autoRedeemed: true })
        );
      } catch (error) {
        heapTrack('Failed to update cart for special items', { err: 'Gift update error' });
        publishChanges(shopifyCartData);
      }
    } else {
      publishChanges(shopifyCartData);
    }
  }
};

export const extractGiftMessageDetails = (cartNote: string) => {
  let note = '';
  let from = '';
  if (cartNote.length > 0) {
    if (cartNote.includes('\nFrom')) {
      from = cartNote.substring(cartNote.indexOf('\nFrom') + 6, cartNote.length).trim();
      note = cartNote.substring(0, cartNote.indexOf('\n')).trim();
    } else {
      note = cartNote;
    }
  }
  return { from, note };
};

export const isGiftCardWithRecipient = (cartItem: CartItem) => {
  return cartItem.gift_card && (cartItem.properties.__shopify_send_gift_card_to_recipient ?? false);
};

// if quantity is 0 (sold out) then display the price of what it would be if quantity was 1
export const showSingleQuantityAmount = (
  quantity: number | undefined,
  amountPerQuantity: number | undefined,
  totalAmount: number
) => {
  return amountPerQuantity && quantity === 0 ? amountPerQuantity : totalAmount;
};

// T-1291 this function can save note on the cart
export const updateNote = async (cartNote: { note: string }, publish?: boolean) => {
  return await cartNoteUpdate(cartNote.note)
    .then((response) => {
      const data = transformCartApiResponse(response?.cart, { warnings: response?.warnings });
      if (data?.items) {
        publish && publishChanges(data);
      }
    })
    .catch((err: Error) => {
      heapTrack('Failed to update cart note', { err: err?.message });
      throw err;
    });
};

// ? [REVISIT] error handling?; promise return values (indicating success or failure)?
// * [IMPROVEMENT] if we pass more data to ourselves (beyond variantId) we can optimize calls: performance vs complexity
// ? [REVISIT] does `/cart/add` provide extra safety checks over `/cart/update`? (ex: like in-stock/inventory checks?)
// Operations ------------------------------------

export const addVariant = async (
  variantId: string,
  variantData: (CartVariantData & { quantityAvailable?: number }) | null,
  options: { shouldShowATCmodal?: boolean; ATBtype?: string } = {},
  ga4options?: ga4options,
  cartItemProperties = {}
) => {
  const { shouldShowATCmodal = true, ATBtype = 'Standard' } = options;
  // variantData && addVariantPricesData([variantData]);
  const cartItemAttributes = transformCartItemAttributesToFitCartLinesAddMutation(cartItemProperties);
  const updates = {} as LineItem;
  updates.cartItemAttributes = cartItemAttributes;
  updates.merchandiseId = Number(variantId);
  updates.quantity = 1;

  const addToCartPromise = new Promise<{ error: AddVariantError; data: ShopifyCartData }>((resolve, reject) => {
    addLineItemToCart([updates])
      .then(async (response) => {
        const data = transformCartApiResponse(response?.cart, { warnings: response?.warnings });
        if (data?.items) {
          const item = data.items.find((product) => product.variant_id === Number(variantId));
          if (item) {
            // ? [REVISIT] Sometimes /cart/add.js returns an empty array; We need to retest it with Cart API
            //  Reply: Adding an item using Cart API always return the updated cart
            fireGA4ecommerceAddOrRemoveProductEvent([item], { ga4options });
            shouldShowATCmodal && dispatchAddedToCartEventWithModal(item);
            await reSyncCart(data);
            dispatchAddedToCartEvent([variantId], ATBtype, data);
            sendHeapTrackingEventForShopTheLookQuickAdd(ATBtype, variantData, data);
          }
        } else {
          // If Sold Out product
          // ? [REVISIT] Once on Cart API
          // Currently we have disable ATC button for outofstock product
          console.warn(data);
        }
        return resolve({ error: null, data });
      })
      .catch((error) => {
        // [REVISIT] ? Once on Cart API. Can be thrown if item is unpublished.
        // This should be fine i guess. Not tested unpublished scenario.
        console.error('addVariant function error: ', error);
        reject({ error, data: null });
      });
  });
  return addToCartPromise;
};

type AddVariantError = null | Error | string | Record<string, string>;

interface BuilderAddToCartTypes {
  product_id: string;
  variant_id: string;
  handle: string;
  quickAddMobile: boolean;
}

export const builderAddToCart = async (detail: BuilderAddToCartTypes) => {
  try {
    const { product_id, handle, quickAddMobile, variant_id } = detail;
    if (IS_NATIVE_APP_CLIENT) {
      window.location.href = prependLocaleToPath(`/webview-atb-action?productId=${product_id}&handle=${handle}`);
    } else {
      const extendedProductData = await fetchExtendedProductData({ handle: handle });
      if (!extendedProductData) return;

      const {
        sizesData,
        singleVariant,
        variantPricesData,
        isAtLeastOneSizeAvailable,
        productData,
      } = extendedProductData;

      if (sizesData.length === 1 && singleVariant?.isSizeAvailable) {
        await addVariant(singleVariant.variantId, variantPricesData);
      } else if (!quickAddMobile) {
        await addVariant(variant_id, variantPricesData);
      } else if (isAtLeastOneSizeAvailable) {
        dispatchOpenVariantSelectorDropdown({
          sizesData: sizesData.map((i) => ({ ...i, sku: i.sku ?? '' })),
          productType: productData.productType,
          variantPricesData,
          shouldShowATCmodal: true,
          ATBtype: 'Standard',
        });
      }
    }
  } catch (err) {
    console.error('Failed to do builder add to cart:', err);
  }
};

export const initializeBuilderAddToCartListener = () => {
  document.addEventListener('BUILDER_ADD_TO_CART', (e: Event) => {
    const eventData = e as CustomEvent<BuilderAddToCartTypes>;
    builderAddToCart({
      variant_id: String(eventData.detail.variant_id),
      handle: eventData.detail.handle,
      quickAddMobile: eventData.detail.quickAddMobile,
      product_id: eventData.detail.product_id,
    });
  });
};

export const changeItemQuantity = async (
  key: string,
  quantity: number,
  isChangeAddition: boolean = false,
  itemProperties?: Attributes
) => {
  // GA4 remove_from_cart event
  const cartData = getCartData();
  const items: ItemGA4[] = [...cartData.regularItems, ...cartData.giftItemsAndLoyaltyRewards].filter(
    (item: ItemGA4) => item.key === key
  );
  items.forEach((item) => {
    if ('displayPrice' in item && item.displayPrice) item.displayPrice = item.displayPrice / 100;
  });
  // End of GA4
  const variantId = splitAtFirstColon(key)[0]!;
  const lineId = splitAtFirstColon(key)[1]!;
  try {
    const addLineItemToCartResponse = await updateCartLineItem([lineId], quantity);
    const newCartData = transformCartApiResponse(addLineItemToCartResponse?.cart, {
      warnings: addLineItemToCartResponse?.warnings,
    });
    if (items && items.length > 0)
      fireGA4ecommerceAddOrRemoveProductEvent(items, {
        isDecrement: !isChangeAddition,
        isRemoving: quantity === 0,
        isCart: true,
      });

    await reSyncCart(newCartData);

    if (isChangeAddition) dispatchAddedToCartEvent([variantId], 'Cart Line Increment', newCartData);
    return newCartData;
  } catch (error) {
    console.error('Error updating cart:', error);
  }
};

//Add set items from cart
export const addSet = (
  variantIds: string[],
  variantData: CartVariantData[],
  properties: SetLineItemProperties,
  ATBtype = 'Standard'
) => {
  const addToCartPromise = new Promise<ShopifyCartData>((resolve, reject) => {
    let lines: LineItems = [];
    const cartItemAttributes = transformCartItemAttributesToFitCartLinesAddMutation(properties ?? {});
    // `reverse()` is for preserving the set order
    lines = variantIds.reverse().map((id) => {
      return {
        merchandiseId: Number(id),
        quantity: 1,
        cartItemAttributes,
      };
    });
    addLineItemToCart(lines).then(async (response) => {
      const data = transformCartApiResponse(response?.cart, { warnings: response?.warnings });
      if (data?.items) {
        const items = data.items.filter((product) => variantIds.includes(String(product.variant_id)));
        // GA4 add_to_cart event
        fireGA4ecommerceAddOrRemoveProductEvent(items, { setTitle: properties?._setName });
        await reSyncCart(data);
        dispatchAddedToCartEvent(variantIds, ATBtype, data);
        return resolve(data);
      }
    });
  });
  return addToCartPromise;
};

//Remove set items from cart
export const removeLineItems = async (keys: string[]) => {
  // GA4 remove_from_cart event
  const cartData = getCartData();
  const items: (CartItem | undefined)[] = [];
  keys.forEach((key) => {
    items.push(cartData.regularItems.find((item) => item.key === key));
  });
  const _items = items.filter((i) => i !== undefined);
  _items.forEach((item) => {
    if (item.displayPrice) item.displayPrice = item.displayPrice / 100;
  });
  if (_items) fireGA4ecommerceAddOrRemoveProductEvent(_items, { isRemoving: true, isCart: true });
  // End of GA4
  const lineIds = transformArrayToObject(keys);
  const addLineItemToCartResponse = await updateCartLineItem(lineIds, 0);
  const newCartData = transformCartApiResponse(addLineItemToCartResponse?.cart, {
    warnings: addLineItemToCartResponse?.warnings,
  });
  await reSyncCart(newCartData);
};

// ? [REVISIT] custom element to avoid dispatching events on the 'document'
export const ADDED_TO_CART_EVENT = 'ADDED TO CART';
// AB test T815
export const ADDED_TO_CART_EVENT_MODAL = 'ADDED TO CART AND OPENED MODAl';

export type ADD_TO_CART_EVENT_DATA = {
  variantIds: string[];
  ATBtype: string;
  newCartData: ShopifyCartData;
};
export const dispatchAddedToCartEvent = (variantIds: string[], ATBtype: string, newCartData: ShopifyCartData) => {
  document.dispatchEvent(
    new CustomEvent<ADD_TO_CART_EVENT_DATA>(ADDED_TO_CART_EVENT, {
      detail: {
        variantIds,
        ATBtype,
        newCartData,
      },
    })
  );
};

// Check if ATBtype is "Shop the look" and send heap tracking event
export const sendHeapTrackingEventForShopTheLookQuickAdd = (
  ATBtype: string,
  variantData: (CartVariantData & { quantityAvailable?: number | undefined }) | null,
  newCartData: ShopifyCartData
) => {
  if (ATBtype === 'Shop the look' && variantData?.productId) {
    const product = newCartData.items?.find((item) => item.product_id === variantData.productId);
    heapTrack('PDP - Click - Shop the Look Product ATC Via Quick Add', {
      'Product title': product?.product_title,
    });
  }
};

const dispatchAddedToCartEventWithModal = (data: AddedToCartProduct) => {
  document.dispatchEvent(new CustomEvent(ADDED_TO_CART_EVENT_MODAL, { detail: data }));
};

// hooks
export const useCartData = ({ watchForChanges } = { watchForChanges: true }) => {
  // To enhance the initial load time. data is loading from local store.
  const initialData: CartData = getCartDataFromLocalStore() ?? getCartData();
  const [cartData, setCartData] = useState(initialData);
  useEffect(() => {
    let isMounted = true;
    const disconnect = watchForChanges
      ? watchCart(() => {
          if (isMounted) setCartData(getCartData());
        })
      : noop;
    return () => {
      isMounted = false;
      disconnect();
    };
  }, [watchForChanges]);
  return cartData;
};

export const gtmAbandonCartAnalyticsSetup = () => {
  let currentCartData = getCartData();
  pushEventToGTMDataLayer({ event: 'alo_currentCartState', currentCartState: currentCartData.regularItems });

  watchCart((newCartData) => {
    const updatedItems =
      currentCartData.count < newCartData.count
        ? differenceBy(
            newCartData.regularItems,
            currentCartData.regularItems,
            (cartItem) => cartItem.key + cartItem.quantity
          )
        : differenceBy(
            currentCartData.regularItems,
            newCartData.regularItems,
            (cartItem) => cartItem.key + cartItem.quantity
          );

    if (updatedItems.length > 0) {
      //account for add or remove or change quantity add - difference is added, removed - difference is removed
      pushEventToGTMDataLayer({ event: 'alo_currentCartState', currentCartState: newCartData.regularItems });
    }
    currentCartData = newCartData;
  });
};

export const GetProductVariantInventoryById = gql`
  query GetProductVariantDetailsByIds($ids: [ID!]!, $countryCode: CountryCode) @inContext(country: $countryCode) {
    nodes(ids: $ids) {
      id
      ... on ProductVariant {
        id
        title
        requiresShipping
        quantityAvailable
        availableForSale
        currentlyNotInStock
      }
    }
  }
`;

gtmAbandonCartAnalyticsSetup();

initializeCartAnalytics();

// Types ------------------------------------
type ItemGA4 = CartItem | GiftItemsAndLoyaltyRewardsCartItem;

type SharedSetLineItemProperties = {
  _setUid: string;
  _setName: string;
  _setUrl: string;
};
export type SetPricingOptionalProperties = {
  _setStrikeThroughPrice?: ShopifyPrice;
  _setForcedMatchingSets?: boolean; // add proper TS constraints for this also or remove this, once we flush out the individual features
};
export type CollapedSetUILineItemProperties =
  | {
      _setCollapedCartUI: true;
      _setImage: string;
    }
  | { _setCollapedCartUI?: false };

export type PreorderEstimatedShipDate = {
  _preorder_estimated_ship_date?: string;
};

// this is why I love TS
export type SetLineItemProperties = SharedSetLineItemProperties &
  SetPricingOptionalProperties &
  CollapedSetUILineItemProperties &
  PreorderEstimatedShipDate;

export interface CartData {
  regularItems: CartItem[];
  giftItemsAndLoyaltyRewards: GiftItemsAndLoyaltyRewardsCartItem[];
  subtotal: number;
  totalSavings: number;
  count: number;
  note: string | null;
  checkoutUrl?: string;
  totalCartItemsQty: number;
}

interface GiftItemsAndLoyaltyRewardsCartItem extends ShopifyCartItem {
  properties: Attributes;
}

export interface GiftWithPurchaseSettings {
  isEnanbledForUSusers: boolean;
  isEnabledForINTLusers: boolean;
  variantId: number; // really an Id / string?
  oldProductIds?: number[];
  threshold: number;
  image: {
    src: string;
    alt: string;
  };
  titlePrefix: string;
  titleAmountColor: string;
  titleSuffix: string;
  message: string;
  productId: number; // really an Id / string?
}

export interface CartItem extends ShopifyCartItem {
  showFinalSaleTreatment: boolean;
  showExchangeOnlyMessage: boolean;
  properties: Attributes;
}

// Server Data Types
export interface ShopifyCartData {
  token: string;
  note: string | null;
  attributes: Attributes;
  original_total_price: number;
  total_price: number;
  total_discount: number;
  item_count: number;
  items: ShopifyCartItem[];
  currency: string;
  items_subtotal_price: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkoutUrl?: string;
}

export type Attributes = Record<string, unknown> & {
  _isGWP?: boolean;
  __lion_context?: string;
  _market?: string;
  _expires?: number;
  _loyaltyClaimRewardId?: number;
  _loyaltyRewardId?: number;
  __shopify_send_gift_card_to_recipient?: boolean;
  'Recipient email'?: string;
  'Recipient name'?: string;
  Message?: string;
  'Send on'?: string;
  _setName?: string;
  _setUid?: string;
  _setUrl?: string;
};

export interface ShopifyCartItem {
  id: number;
  properties?: Attributes | null | undefined;
  quantity: number;
  variant_id: number;
  key: string;
  title: string;
  price: number;
  original_price: ShopifyPrice;
  discounted_price: ShopifyPrice;
  line_price: ShopifyPrice;
  original_line_price: ShopifyPrice;
  total_discount: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  discounts: any[];
  sku: string;
  vendor: string;
  product_id: number;
  gift_card: boolean;
  final_price: ShopifyPrice;
  final_line_price: ShopifyPrice;
  url: string;
  featured_image: FeaturedImage;
  image: string;
  handle: string;
  requires_shipping: boolean;
  product_type: string;
  product_title: string;
  product_description: string;
  variant_title: string;
  variant_options: string[];
  options_with_values: OptionsWithValue[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  line_level_discount_allocations: any[];
  displayPrice: number | undefined;
  tags: string[];
  collections: string[];
  quantityAvailable?: number;
  estimatedShipStartDate: string | null;
  line_id: string;
  // to handle special case when quantity is zero (sold out online) in cart. in this case amountPerQuantity will be shown.
  amountPerQuantity?: number;
}

export type ShopifyPrice = number;

export interface FeaturedImage {
  url: string;
  alt: string;
}

export interface OptionsWithValue {
  name: string;
  value: string;
}

// ? [REVISIT] eventually have a shared space common Shopify types & (potentially) top level js file to pull each top level shopify equivalent (`./customer`)
export interface ShopifySettings {
  enable_gift: boolean;
  enable_gift_us: boolean;
  enable_gift_intl: boolean;
  enable_gift_beauty_us: boolean;
  enable_gift_beauty_intl: boolean;
  enable_cart_upselling_block: boolean;
  gift_variant_id: string;
  amount_to_spend_color: string;
  gift_block_title: string;
  amount_to_spend: string;
  gift_block_title_2: string;
  gift_block_text: string;
  gift_block_image: string;
  image_alt_text: string;
  enable_gift_beauty: boolean;
  amount_to_spend_beauty: string;
  gift_beauty_in_cart_message: string;
  gift_beauty_product: string;
  gift_beauty_variant_id: string;
  gift_beauty_old_product_ids: string;
  gift_beauty_block_title: string;
  gift_beauty_block_title_2: string;
  gift_beauty_block_text: string;
  image_alt_text_beauty: string;
  beauty_amount_to_spend_color: string;
  gift_beauty_block_image: string;
  email_modal_enable: boolean;
  email_modal_title: string;
  email_modal_subtitle: string;
  email_modal_geo: boolean;
  email_modal_geo_targets: string;
  email_modal_geo_title: string;
  email_modal_geo_subtitle: string;
  social_twitter_link: string;
  social_facebook_link: string;
  social_pinterest_link: string;
  social_instagram_link: string;
  social_youtube_link: string;
  social_google_link: string;
  favicon: string;
  customer_service: string;
  studios: string;
  pro_program: string;
  checkout_header_image: null;
  checkout_logo_image: string;
  checkout_logo_position: string;
  checkout_logo_size: string;
  checkout_body_background_image: null;
  checkout_body_background_color: string;
  checkout_input_background_color_mode: string;
  checkout_sidebar_background_image: null;
  checkout_sidebar_background_color: string;
  checkout_heading_font: string;
  checkout_body_font: string;
  checkout_accent_color: string;
  checkout_button_color: string;
  checkout_error_color: string;
  top_links_to_hide: string;
  alomoves_enable: boolean;
  red_sale: boolean;
  shipping_banner_enable: boolean;
  shipping_banner_text: string;
  shipping_banner_cta_text: string;
  shipping_banner_cta_link: string;
  shipping_banner_bg_color: string;
  shipping_banner_text_color: string;
  countdown_banner_enable: boolean;
  countdown_banner_bg: string;
  text_banner_enable: boolean;
  text_banner_bg: string;
  text_banner_text_color: string;
  text_banner_text: string;
  universal_with_image_enable: boolean;
  universal_with_image_bg_image: string;
  universal_with_image_bg_image_mobile: string;
  universal_with_image_link: string;
  universal_with_image_bg_color: string;
  universal_with_image_bg_img: string;
  universal_with_image_map_html: string;
  universal_with_image_map_name: string;
  'universal_with_image_map_html-mobile': string;
  'universal_with_image_map_name-mobile': string;
  'universal_with_image-2_enable': boolean;
  'universal_with_image_bg_image-2': string;
  'universal_with_image_bg_image-2_mobile': string;
  'universal_with_image-2_link': string;
  'universal_with_image-2_bg_color': string;
  'universal_with_image_bg_img-2': string;
  'universal_with_image-2_map_html': string;
  'universal_with_image-2_map_name': string;
  'universal_with_image-2_map_html-mobile': string;
  'universal_with_image-2_map_name-mobile': string;
  universal_banner_enable: boolean;
  universal_banner_bg_color: string;
  universal_banner_text_color: string;
  universal_banner_cta_1: string;
  universal_banner_cta_2: string;
  universal_banner_link_1: string;
  universal_banner_link_2: string;
  'universal_banner-3_enable': boolean;
  'universal_banner-3_bg_color': string;
  'universal_banner-3_text_color': string;
  'universal_banner-3_cta_1': string;
  'universal_banner-3_cta_2': string;
  'universal_banner-3_cta_3': string;
  'universal_banner-3_link_1': string;
  'universal_banner-3_link_2': string;
  'universal_banner-3_link_3': string;
  'universal_banner-4_enable': boolean;
  'universal_banner-5_enable': boolean;
  secret_modal_enable: boolean;
  secret_modal_title: string;
  secret_modal_handle: string;
  secret_modal_button: string;
  teaser_image: string;
  teaser_mobile_image: string;
  teaser_alt: string;
  teaser_link: string;
  palisades_enable: boolean;
  cart_notes_enable: boolean;
  borderfree_enabled: boolean;
  borderfree_js_snippet: string;
  type_base_size: string;
  color_primary: string;
  color_body_text: string;
  social_sharing_blog: boolean;
  share_facebook: boolean;
  share_twitter: boolean;
  share_pinterest: boolean;
  social_snapchat_link: string;
  social_tumblr_link: string;
  social_vimeo_link: string;
  customer_layout: string;
  enable_cart_sale_messaging: boolean;
  cart_sale_messaging_exclusion_tag: string;
  cart_sale_messaging_line_item_copy: string;
  hide_gwp_carousel: boolean;
}

export interface CartVariantData {
  id: number;
  price: ShopifyPrice;
  displayPrice: ShopifyPrice | null | undefined;
  tags: string[];
  collections?: string[];
  productId: number;
}

export interface AddingResponse {
  items: Item[];
  description?: string;
  message?: string;
  status?: number;
}

export interface Item {
  id: number;
  properties: Properties;
  quantity: number;
  variant_id: number;
  key: string;
  title: string;
  price: number;
  original_price: number;
  discounted_price: number;
  line_price: number;
  original_line_price: number;
  total_discount: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  discounts: any[];
  sku: string;
  vendor: string;
  product_id: number;
  gift_card: boolean;
  final_price: number;
  final_line_price: number;
  url: string;
  featured_image: FeaturedImage;
  image: string;
  handle: string;
  requires_shipping: boolean;
  product_type: string;
  product_title: string;
  product_description: string;
  variant_title: string;
  variant_options: string[];
  options_with_values: OptionsWithValue[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  line_level_discount_allocations: any[];
  line_level_total_discount: number;
}

export interface FeaturedImage {
  alt: string;
  url: string;
}

export interface OptionsWithValue {
  name: string;
  value: string;
}

export interface Properties {}

export interface GiftDataResponse {
  data?: GiftData;
}

export interface GiftData {
  nodes: GiftDataNode[];
}

export interface GiftDataNode {
  id: string;
  title: string;
  requiresShipping: boolean;
  quantityAvailable: number;
  availableForSale: boolean;
  currentlyNotInStock: boolean;
  __typename: string;
}
